import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import NumberFormat from "react-number-format";
import { Skeleton } from 'antd';

import { RELATIONSHIPS } from '../../constants';
import useCustomerLoanSummary from './useCustomerLoanSummary';
import './Crm.css';


const MARITAL_STATUSES = {
    SINGLE: 'Single',
    MARRIED: 'Married',
    DIVORCED: 'Divorced',
    WIDOWED: 'Widowed',
};

const ADDRESS_TYPE_CLASS = {
    RESIDENTIAL: 'co-chip-blue',
    BUSINESS: 'co-chip-purple',
};

export const initials = (name) =>
    (name || '')
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word[0])
        .join('')
        .toUpperCase();

const titleCase = (value) =>
    typeof value === 'string' && value
        ? value.charAt(0) + value.slice(1).toLowerCase()
        : '';

export const formatDate = (value, pattern) => {
    if (!value) return '';
    const date = new Date(value);
    return isNaN(date.getTime()) ? '' : format(date, pattern);
};

const formatAmount = (currency, amount) =>
    `${currency} ${Number(amount || 0).toLocaleString()}`;

const formatPercent = (rate) => `${Math.round(Number(rate))}%`;

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// Next-instalment countdown is frontend-side: the API sends the due date,
// not a day count. Calendar days, so a due date "tomorrow" reads as 1 day
// regardless of what time of day the page is opened.
const daysUntil = (isoDate) =>
    differenceInCalendarDays(parseISO(isoDate), new Date());

const dueLabel = (days) => {
    if (days === 0) return ' \u00b7 due today';
    return ` \u00b7 in ${plural(days, 'day')}`;
};

// The serializer may expand `country` or return a bare FK id. A raw id is
// worse than no row, so only render a resolvable name.
const countryName = (customer) => {
    if (customer.country_name) return customer.country_name;
    if (customer.country && typeof customer.country === 'object') {
        return customer.country.name || '';
    }
    return '';
};


const CustomerView = ({ customer, variant = 'full' }) => {
    // Always called — never inside the variant branch (rules of hooks).
    // The applicant view renders neither loan block, so it gates the query
    // through `enabled` instead: the hook runs, the request does not.
    const summary = useCustomerLoanSummary(customer?.id, {
        enabled: variant !== 'applicant',
    });

    if (!customer) return null;

    const isApplicant = variant === 'applicant';

    return (
        <section className="co-page">
            {!isApplicant && summary.loansEnabled !== false && (
                <>
                    <CurrentLoanCard summary={summary} />
                    <StatBand summary={summary} />
                </>
            )}

            <IdentityPanel customer={customer} />

            <div className="co-columns">
                <AddressesPanel addresses={customer.addresses} />
                <RelatedPartiesPanel parties={customer.related_parties} />
            </div>
        </section>
    );
};

export default CustomerView;


const CurrentLoanCard = ({ summary }) => {
    const { currency, current_loan: loan, isLoading, isError } = summary;

    if (isLoading) {
        return (
            <div className="co-panel co-loan">
                <div className="co-skeleton-loan">
                    <Skeleton active paragraph={{ rows: 3, width: ['40%', '65%', '85%'] }} />
                </div>
                <div className="co-loan-next co-skeleton-next">
                    <Skeleton active paragraph={{ rows: 2, width: ['70%', '85%'] }} />
                </div>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="co-panel">
                <div className="co-empty">Loan summary unavailable</div>
            </div>
        );
    }

    // `current_loan: null` means no live loan case — not an error, and not a
    // reason to hide the card. The tiles below still have lifetime figures.
    if (!loan) {
        return (
            <div className="co-panel co-loan">
                <div>
                    <div className="co-loan-kicker">
                        <span className="co-loan-kicker-text">Current loan</span>
                    </div>
                    <div className="co-loan-amount co-loan-empty">No active loan</div>
                </div>
            </div>
        );
    }

    const progress = Number(loan.total_due)
        ? Math.min(100, Math.round((Number(loan.repaid) / Number(loan.total_due)) * 100))
        : 0;

    const days = loan.next_instalment_due ? daysUntil(loan.next_instalment_due) : null;
    const isOverdue = days !== null && days < 0;

    return (
        <div className="co-panel co-loan">
            <div>
                <div className="co-loan-kicker">
                    <span className="co-loan-kicker-text">
                        Current loan · {loan.product_name}
                    </span>
                    <span className="co-chip co-chip-blue">{loan.status_display}</span>
                </div>

                <div className="co-loan-amount">
                    {formatAmount(currency, loan.outstanding)}
                    <span>outstanding</span>
                </div>
                <div className="co-loan-repaid">
                    {formatAmount(currency, loan.repaid)} repaid of {formatAmount(currency, loan.total_due)}
                </div>

                <div className="co-progress">
                    <div className="co-progress-bar" style={{ width: `${progress}%` }} />
                </div>

                <div className="co-loan-footer">
                    <span>
                        {loan.instalments_paid} of {loan.instalments_total} instalments paid
                    </span>
                    <span>
                        Disbursed {formatDate(loan.disbursement_date, 'dd MMM yyyy')} · Matures {formatDate(loan.maturity_date, 'dd MMM yyyy')}
                    </span>
                </div>
            </div>

            <div className="co-loan-next">
                <div className="co-loan-kicker-text">Next instalment</div>
                {loan.next_instalment_due
                    ? <>
                          <div className="co-loan-next-amount">
                              {formatAmount(currency, loan.next_instalment_amount)}
                          </div>
                          <div className="co-loan-next-due">
                              Due {formatDate(loan.next_instalment_due, 'dd MMM yyyy')}
                              {isOverdue
                                  ? <div style={{ marginTop: 10 }}>
                                        <span className="co-chip co-chip-red">
                                            {plural(Math.abs(days), 'day')} overdue
                                        </span>
                                    </div>
                                  : <span>{dueLabel(days)}</span>
                              }
                          </div>
                      </>
                    : <div className="co-empty" style={{ marginTop: 14 }}>None scheduled</div>
                }
            </div>
        </div>
    );
};


const STAT_LABELS = [
    'On-time repayment',
    'Lifetime borrowed',
    'Earnings to date',
    'Last payment',
];

const StatBand = ({ summary }) => {
    const {
        currency,
        isLoading,
        isError,
        on_time_rate: onTimeRate,
        on_time_paid: onTimePaid,
        on_time_total: onTimeTotal,
        lifetime_borrowed: lifetimeBorrowed,
        loans_count: loansCount,
        loans_completed: loansCompleted,
        earnings_to_date: earningsToDate,
        last_payment_amount: lastPaymentAmount,
        last_payment_date: lastPaymentDate,
        last_payment_channel: lastPaymentChannel,
    } = summary;

    if (isLoading) {
        // Labels stay put while the figures load, so only the two lines
        // beneath them are stood in for and the band keeps its height.
        return (
            <div className="co-stats">
                {STAT_LABELS.map((label) => (
                    <div className="co-panel co-stat" key={label}>
                        <div className="co-stat-label">{label}</div>
                        <div className="co-skeleton-stat">
                            <Skeleton
                                active
                                title={false}
                                paragraph={{ rows: 2, width: ['70%', '90%'] }}
                            />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="co-stats">
                <div className="co-panel co-stats-error">
                    <div className="co-empty">Repayment figures unavailable</div>
                </div>
            </div>
        );
    }

    const cells = [
        {
            label: 'On-time repayment',
            // null rate means nothing has fallen due yet — an em dash, not 0%.
            value: onTimeRate === null || onTimeRate === undefined
                ? '—'
                : formatPercent(onTimeRate),
            sub: `${onTimePaid} of ${onTimeTotal} instalments`,
            good: onTimeRate !== null && onTimeRate !== undefined,
        },
        {
            label: 'Lifetime borrowed',
            value: formatAmount(currency, lifetimeBorrowed),
            sub: `${plural(loansCount, 'loan')} · ${loansCompleted} completed`,
        },
        {
            label: 'Earnings to date',
            value: formatAmount(currency, earningsToDate),
            sub: 'total interest & fees',
        },
        {
            label: 'Last payment',
            // null amount means no repayment has ever posted.
            value: lastPaymentAmount === null || lastPaymentAmount === undefined
                ? 'No payments yet'
                : formatAmount(currency, lastPaymentAmount),
            empty: lastPaymentAmount === null || lastPaymentAmount === undefined,
            sub: [formatDate(lastPaymentDate, 'dd MMM yyyy'), lastPaymentChannel]
                .filter(Boolean)
                .join(' · '),
        },
    ];

    return (
        <div className="co-stats">
            {cells.map((cell) => (
                <div className="co-panel co-stat" key={cell.label}>
                    <div className="co-stat-label">{cell.label}</div>
                    <div
                        className={`co-stat-value${cell.good ? ' co-stat-good' : ''}${cell.empty ? ' co-stat-empty' : ''}`}
                    >
                        {cell.value}
                    </div>
                    <div className="co-stat-sub">{cell.sub}</div>
                </div>
            ))}
        </div>
    );
};


const Field = ({ label, value }) => {
    if (!value) return null;
    return (
        <div>
            <div className="co-field-label">{label}</div>
            <div className="co-field-value">{value}</div>
        </div>
    );
};


const IdentityPanel = ({ customer }) => {
    const telephone = customer.telephone
        ? <NumberFormat
              value={customer.telephone}
              displayType="text"
              format="### (###) ######"
          />
        : null;

    const gender = { M: 'Male', F: 'Female' }[customer.gender] || '';

    return (
        <div className="co-panel">
            <div className="co-panel-header">
                <span className="co-panel-title">Identity &amp; Contact</span>
            </div>

            <div className="co-identity">
                <Field label="Telephone" value={telephone} />
                <Field label="Email" value={customer.email} />

                {customer.is_person &&
                    <>
                        <Field label="Date of Birth" value={formatDate(customer.birth_date, 'dd MMM yyyy')} />
                        <Field label="Gender" value={gender} />
                        <Field label="Marital Status" value={MARITAL_STATUSES[customer.marital_status]} />
                    </>
                }

                <Field label="Nationality" value={countryName(customer)} />

                {customer.is_company
                    ? <>
                          <Field label="Company Registration No" value={customer.registration_number} />
                          <Field label="Website" value={customer.website} />
                      </>
                    : <Field label="National ID" value={customer.id_number} />
                }
            </div>
        </div>
    );
};


const AddressesPanel = ({ addresses }) => {
    const rows = addresses || [];

    return (
        <div className="co-panel">
            <div className="co-panel-header">
                <span className="co-panel-title">Addresses</span>
            </div>

            {rows.length === 0
                ? <div className="co-empty">No addresses recorded</div>
                : <div className="co-rows">
                      {rows.map((address, index) => (
                          <div className="co-row" key={address.id || index}>
                              <span className={`co-chip co-chip-type ${ADDRESS_TYPE_CLASS[address.address_type] || 'co-chip-slate'}`}>
                                  {address.address_type}
                              </span>
                              <div className="co-row-main">
                                  <div className="co-row-primary">{address.address_line_1}</div>
                                  {address.address_line_2 &&
                                      <div className="co-row-secondary">{address.address_line_2}</div>
                                  }
                              </div>
                              <div className="co-row-aside">{address.city}</div>
                          </div>
                      ))}
                  </div>
            }
        </div>
    );
};


const RelatedPartiesPanel = ({ parties }) => {
    const rows = parties || [];

    return (
        <div className="co-panel">
            <div className="co-panel-header">
                <span className="co-panel-title">Related Parties</span>
            </div>

            {rows.length === 0
                ? <div className="co-empty">No related parties recorded</div>
                : <div className="co-rows">
                      {rows.map((party, index) => {
                          // The read shape is unconfirmed: `contact` may be a
                          // nested object, a bare id, or flattened onto the row.
                          const nested = party.contact && typeof party.contact === 'object';
                          const contact = nested ? party.contact : party;
                          const contactId = nested ? party.contact.id : party.contact;
                          const name = contact.name || `Contact #${contactId ?? party.id ?? '?'}`;
                          const phone = contact.telephone || contact.phone;
                          const relationship =
                              RELATIONSHIPS.find((r) => r.value === party.relationship)?.label
                              || titleCase(party.relationship);

                          return (
                              <div className="co-row" key={party.id || index}>
                                  <div className="co-party-avatar">{initials(contact.name)}</div>
                                  <div className="co-row-main">
                                      <div className="co-party-name">
                                          {name}
                                          {relationship && <span> · {relationship}</span>}
                                      </div>
                                      {(phone || contact.email) &&
                                          <div className="co-party-contact">
                                              {[phone, contact.email].filter(Boolean).join(' · ')}
                                          </div>
                                      }
                                  </div>
                              </div>
                          );
                      })}
                  </div>
            }
        </div>
    );
};
